// Identity domain constants: roles, claims, scopes and auth mechanics.
// User-visible copy lives in common/copy.js.

export const Auth = {
  DefaultProfilePhoto:
    "https://cdn.pellerex.com/public/ecosystem/web/profile/pellerex-profile-avatar-thumbnail.png",
  PasswordRegEx: /^(?=.*[A-Z])(?=.*[\W])(?=.*[0-9])(?=.*[a-z]).{8,128}$/,
  TokenHolderCookieName: "pellerex-external-auth",
  Audiences: {
    InternalEcosystemServiceProvider: "internal-ecosystem-service-provider",
    ManagedApi: "ecosystem-managed-api",
    ManagedApp: "ecosystem-managed-app",
    ManagedServerlessApp: "ecosystem-managed-serverless-app",
    ManagedContainerApp: "ecosystem-managed-container-app",
  },
  // What each of Pellerex's own identities that may call customers' apps is for, as IdentityApi
  // names them (CommonLayer's ManagedIdentityPurpose).
  ManagedIdentityPurposes: {
    HealthChecks: "health-checks",
    TestRuns: "test-runs",
  },
  RoleLevel: {
    Tenant: {
      Code: "Tenant",
    },
    AppSpace: {
      Code: "AppSpace",
    },
    App: {
      Code: "App",
    },
    Organisation: {
      Code: "Organisation",
    },
    Universe: {
      Code: "Universe",
    },
  },
  Roles: {
    OrganisationContributor: {
      Code: "ORGANISATION-CONTRIBUTOR",
      Name: "OrganisationContributor",
    },
    AppReader: {
      Code: "APP-READER",
      Name: "AppReader",
    },
    AppSpaceOwner: {
      Code: "APPSPACE-OWNER",
      Name: "AppSpaceOwner",
    },
    AppOwner: {
      Code: "APP-OWNER",
      Name: "AppOwner",
    },
    TenantOwner: {
      Code: "TENANT-OWNER",
      Name: "TenantOwner",
    },
    OrganisationOwner: {
      Code: "ORGANISATION-OWNER",
      Name: "OrganisationOwner",
    },
    OrganisationReader: {
      Code: "ORGANISATION-READER",
      Name: "OrganisationReader",
    },
    AppSpaceContributor: {
      Code: "APPSPACE-CONTRIBUTOR",
      Name: "AppSpaceContributor",
    },
    AppContributor: {
      Code: "APP-CONTRIBUTOR",
      Name: "AppContributor",
    },
    AppSpaceReader: {
      Code: "APPSPACE-READER",
      Name: "AppSpaceReader",
    },
    SystemUser: {
      Code: "SYSTEM-USER",
      Name: "SystemUser",
    },
    BasicUser: {
      Code: "BASIC-USER",
      Name: "BasicUser",
    },
    AppManager: {
      Code: "APP-MANAGER",
      Name: "AppManager",
    },
    TenantContributor: {
      Code: "TENANT-CONTRIBUTOR",
      Name: "TenantContributor",
    },
    TenantReader: {
      Code: "TENANT-READER",
      Name: "TenantReader",
    },
    SystemOwner: {
      Code: "SYSTEM-OWNER",
      Name: "SystemOwner",
    },
  },
  RoleSuffix: {
    Owner: {
      Code: "OWNER",
      Name: "Owner",
    },
    Contributor: {
      Code: "CONTRIBUTOR",
      Name: "Contributor",
    },
    Reader: {
      Code: "READER",
      Name: "Reader",
    },
  },
  RoleSuffixes: [
    {
      key: "OWNER",
      text: "Owner",
      value: "OWNER",
    },
    {
      key: "CONTRIBUTOR",
      text: "Contributor",
      value: "CONTRIBUTOR",
    },
    {
      key: "READER",
      text: "Reader",
      value: "READER",
    },
  ],
  Scopes: {
    viewProfile: {
      normalisedName: "view:profile",
      name: "View Profile",
      claims: {
        readProfile: "r-profile",
      },
    },
    viewEmail: {
      normalisedName: "view:email",
      name: "View Email",
      claims: {
        readEmail: "r-email",
      },
    },
    manageApp: {
      normalisedName: "manage:app",
      name: "Manage App",
      claims: {
        ReadApp: "r-app",
        CruApp: "cru-app",
        CrudApp: "crud-app",
        ReadAppProdSecrets: "r-app-prod-secrets",
        CruAppProdSecrets: "cru-app-prod-secrets",
        CrudAppProdSecrets: "crud-app-prod-secrets",
        ReadAppNonProdSecrets: "r-app-non-prod-secrets",
        CruAppProdNonSecrets: "cru-app-non-prod-secrets",
        CrudAppNonProdSecrets: "crud-app-non-prod-secrets",
        ReadAppNonProdEnvironments: "r-app-non-prod-envs",
        CruAppNonProdEnvironments: "cru-app-non-prod-envs",
        CrudAppNonProdEnvironments: "crud-app-non-prod-envs",
        ReadAppProdEnvironments: "r-app-prod-envs",
        CruAppProdEnvironments: "cru-app-prod-envs",
        CrudAppProdEnvironments: "crud-app-prod-envs",
        ReadAppUsers: "r-app-users",
        CruAppUsers: "cru-app-users",
        CrudAppUsers: "crud-app-users",
        ReadAppPat: "r-app-pat",
        CruAppPat: "cru-app-pat",
        CrudAppPat: "crud-app-pat",
        CallManagedApp: "call-managed-app",
        ReadAppMcp: "r-app-mcp",
        CruAppMcp: "cru-app-mcp",
        CrudAppMcp: "crud-app-mcp",
        ReadAppToggles: "r-app-toggles",
        CruAppNonProdToggles: "cru-app-non-prod-toggles",
        CruAppProdToggles: "cru-app-prod-toggles",
        CrudAppToggles: "crud-app-toggles",
      },
    },
    manageAppSpace: {
      normalisedName: "manage:appspace",
      name: "Manage AppSpace",
      claims: {
        readAppSpace: "r-app-space",
        crudAppSpace: "crud-app-space",
        cruAppSpace: "cru-app-space",
        readAppSpaceApps: "r-app-space-apps",
        crudAppSpaceApps: "crud-app-space-apps",
        cruAppSpaceApps: "cru-app-space-apps",
        readAppSpaceUsers: "r-app-space-users",
        crudAppSpaceUsers: "crud-app-space-users",
        cruAppSpaceUsers: "cru-app-space-users",
      },
    },
    manageOrg: {
      normalisedName: "manage:org",
      name: "Manage Organisation",
      claims: {
        readOrganisation: "r-org",
        crudOrganisation: "crud-org",
        cruOrganisation: "cru-org",
        readOrganisationAppSpaces: "r-org-app-spaces",
        crudOrganisationAppSpaces: "crud-org-app-spaces",
        cruOrganisationAppSpaces: "cru-org-app-spaces",
        readOrgansationSubscriptions: "r-org-subscriptions",
        crudOrganisationSubscriptions: "crud-org-subscriptions",
        cruOrganisationSubscriptions: "cru-org-subscriptions",
        readOrganisationUsers: "r-org-users",
        crudOrganisationUsers: "crud-org-users",
        cruOrganisationUsers: "cru-org-users",
      },
    },
    manageTenant: {
      normalisedName: "manage:tenant",
      name: "Manage Tenant",
      claims: {
        readTenant: "r-tenant",
        cruTenant: "cru-tenant",
        crudTenant: "crud-tenant",
        viewOrgSubscriptions: "view-org-subscriptions",
        crudOrgSubscription: "crud-org-subscriptions",
        readTenantOrgs: "r-tenant-orgs",
        crudTenantOrgs: "crud-tenant-orgs",
        cruTenantOrgs: "cru-tenant-orgs",
        readTenantUsers: "r-tenant-users",
        crudTenantUsers: "crud-tenant-users",
        cruTenantUsers: "cru-tenant-users",
      },
    },
    manageUniverse: {
      normalisedName: "manage:universe",
      name: "Manage Universe",
      claims: {
        crudUniverseTenants: "crud-universe-tenants",
        crudUsers: "crud-users",
        cruUsers: "cru-users",
        readUsers: "r-users",
        readSystemSettings: "r-system-settings",
      },
    },
  },
  Claims: {
    SessionId: "sid",
    Email: "email",
    UserId: "sub",
    AuthenticationMethod: "amr",
    Issuer: "iss",
  },
  // New product access tokens (an app's Identity tab). A copy of the longest life Identity gives one:
  // it refuses an expiry more than 365 days after its own clock in UTC
  // (Api/Validators/ProductAccessTokenRequestViewModelValidator.cs of pellerex.ecosystem.core.identityapi,
  // the limit beside ProductAccessTokenLoginExpiryInMinutes in Common/IdentityConstants.cs). Identity
  // stays the authority: a change there changes this copy in the same piece of work.
  ProductAccessTokens: {
    LongestLifeInDays: 365,
  },
  MFAMethod: {
    SMS: {
      Key: "SMS",
      Text: "SMS",
      Value: "SMS",
    },
    Email: {
      Key: "Email",
      Text: "Email",
      Value: "Email",
    },
    AuthenticatorApp: {
      Key: "AuthenticatorApp",
      Text: "AuthenticatorApp",
      Value: "AuthenticatorApp",
    },
  },
  AuthenticationMethods: {
    TwoFactorAuth: "mfa",
  },
};
